import type { PartnerConfig } from "@expert/app-config/types";
import type { ServiceProvider } from "@expert/service-providers";

import { getLogger } from "@expert/logging";
import { useExpertWorkspaceWebSocket } from "@expert/websocket";
import { createContext, useEffect, useRef, useState } from "react";

import { updateDispatcherPartner } from "@/analytics";
import { LogFlow } from "@/sdk/logging/flows";
import { type CreateSdkParams, createAgentSDK } from "@/utils/createAgentSdk";

import type { AgentSdk } from "../agentSdk";

import { EvlFailedToConnectError } from "../../errorTypes";
import { attemptCcpInitReload, clearCcpInitRecoveryAttempts, isCcpRegionValidationError } from "../ccpInitRecovery";
import { useStatelessChatCallbacks } from "../chat/chatEventCallbacks";
import { getStatelessChatMessagesCache } from "../chat/getStatelessChatMessagesCache";
import { useExpertUniversitySdk } from "./useExpertUniversitySdk";
import { useIsTrainingMode } from "./useIsTrainingMode";

const logger = getLogger({ module: "useAgentSdkLoader" });

/**
 * If the workspace loader is still showing after this long, emit a warn log. This changes no UI
 * behavior — the loader keeps waiting — it only makes an expert stuck on "Getting things ready..."
 * visible in production logs, with metadata identifying which gate stalled (SDK creation vs. the
 * training-mode wrapper).
 */
const SDK_LOAD_HANG_WARN_MS = 20_000;

export const AgentSdkContext = createContext<AgentSdk | undefined>(undefined);

export type AgentSdkProviderCreator = (params: CreateSdkParams) => Promise<AgentSdk>;

export interface AgentSdkLoaderProps {
    employeeNum: string | null;
    userFriendlyName?: string;
    serviceProvider: ServiceProvider;
    partnerConfig?: PartnerConfig;
    createContainer: (name: string) => Promise<HTMLElement>;
    onAuthRedirect?: VoidFunction;
    skip?: boolean;
    isTrainingModeAllowed?: boolean;
}

export type AgentSdkFailure = "none" | "general" | "evl-failed";

export interface AgentSdkLoaderResult {
    failureType: AgentSdkFailure;
    errorMessage?: string;
    loadingMessage?: string;
    sdk?: AgentSdk;
}

function getAudioProvider(serviceProvider: ServiceProvider, partnerConfig?: PartnerConfig) {
    return serviceProvider === "AMAZON_CONNECT" && partnerConfig?.features.medialess ? "CCP" : "EVL";
}

export function useAgentSdkLoader({
    employeeNum,
    userFriendlyName,
    serviceProvider,
    partnerConfig,
    createContainer,
    onAuthRedirect,
    skip = false,
    isTrainingModeAllowed = false,
}: AgentSdkLoaderProps): AgentSdkLoaderResult {
    const [isReady, setIsReady] = useState(false);
    const [errorInitializingMsg, setErrorInitializingMsg] = useState<string | undefined>(undefined);
    const [agentSdk, setAgentSdk] = useState<AgentSdk | undefined>(undefined);
    const [failureType, setFailureType] = useState<AgentSdkFailure>("none");
    const chatMessagesCache = getStatelessChatMessagesCache();
    const chatEventCallbacks = useStatelessChatCallbacks();
    const webSocket = useExpertWorkspaceWebSocket();
    const isTrainingMode = useIsTrainingMode(isTrainingModeAllowed, agentSdk);
    const sdk = useExpertUniversitySdk(isTrainingMode, agentSdk);
    const audioProvider = getAudioProvider(serviceProvider, partnerConfig);
    const loaderShownAtRef = useRef<number | null>(null);

    useEffect(() => {
        const fetchAgentSdk = async (): Promise<{
            agentSdk?: AgentSdk;
            errorInitializing?: string;
            failureType: AgentSdkFailure;
            isRegionValidationError?: boolean;
        }> => {
            try {
                const sdkRes = await createAgentSDK({
                    createContainer,
                    employeeNum,
                    userFriendlyName,
                    serviceProvider,
                    webSocket,
                    chatMessagesCache,
                    chatEventCallbacks,
                    onAuthRedirect,
                });

                return { agentSdk: sdkRes, failureType: "none" };
            } catch (error) {
                if (error instanceof EvlFailedToConnectError) {
                    return { errorInitializing: error.message, failureType: "evl-failed" };
                }

                return {
                    errorInitializing: error instanceof Error ? error.message : JSON.stringify(error),
                    failureType: "general",
                    isRegionValidationError: isCcpRegionValidationError(error),
                };
            }
        };

        let ignore = false;
        if (!agentSdk && !skip) {
            setIsReady(false);

            const logContext = {
                flow: LogFlow.WorkspaceLoading,
                serviceProvider,
                audioProvider,
                employeeId: employeeNum,
            };
            const startedAt = performance.now();
            logger.info(logContext, "Agent SDK load started");

            fetchAgentSdk()
                .then((res) => {
                    if (!ignore) {
                        const durationMs = Math.round(performance.now() - startedAt);
                        if (res.agentSdk) {
                            // Successful load — clear any CCP reload-recovery counter (#7073).
                            clearCcpInitRecoveryAttempts();
                            logger.info({ ...logContext, durationMs }, "Agent SDK load succeeded");
                            setAgentSdk(res.agentSdk);
                            setFailureType(res.failureType);
                            setIsReady(true);
                        } else if (res.errorInitializing) {
                            // ACGR "Invalid region us-west-2" ValidationException (#7073): a full page
                            // reload usually recovers. Amazon Connect only — this hook also loads
                            // Twilio / Expert University, and the region-reload behavior is CCP-specific.
                            // Attempt a bounded reload before surfacing the error; if it reloads, stop
                            // here (navigation is imminent).
                            if (
                                serviceProvider === "AMAZON_CONNECT" &&
                                res.isRegionValidationError &&
                                attemptCcpInitReload(logger, {
                                    ...logContext,
                                    durationMs,
                                    error: res.errorInitializing,
                                })
                            ) {
                                return;
                            }

                            // evl-failed is a known, handled connection failure with its own error
                            // page; anything else is unexpected and warrants error severity.
                            if (res.failureType === "evl-failed") {
                                logger.warn(
                                    { ...logContext, durationMs, error: res.errorInitializing },
                                    "Agent SDK failed to connect to EVL",
                                );
                            } else {
                                logger.error(
                                    {
                                        ...logContext,
                                        durationMs,
                                        failureType: res.failureType,
                                        error: res.errorInitializing,
                                    },
                                    "Agent SDK load failed",
                                );
                            }
                            setFailureType(res.failureType);
                            setErrorInitializingMsg(res.errorInitializing);
                        }
                    }
                })
                .catch((ex: unknown) => {
                    if (!ignore) {
                        const error = ex instanceof Error ? ex.message : JSON.stringify(ex);
                        logger.error(
                            { ...logContext, durationMs: Math.round(performance.now() - startedAt), error },
                            "Agent SDK load threw unexpectedly",
                        );
                        setErrorInitializingMsg(error);
                    }
                });
        }

        return () => {
            ignore = true;
        };
        // oxlint-disable-next-line exhaustive-deps
    }, [agentSdk, employeeNum, userFriendlyName, serviceProvider, createContainer, skip]);

    // Watchdog for both loading gates: SDK creation (agentSdk) and the training-mode wrapper (sdk).
    // If the loader is still showing after the timeout, warn — the isReady/hasAgentSdk/hasSdk flags
    // identify which gate stalled. Log-only; it does not change loader behavior.
    //
    // The effect re-runs on each gate transition, so the timer is anchored to when the loader FIRST
    // appeared (loaderShownAtRef) and rescheduled for the time remaining. Restarting a fresh timeout
    // each transition would let a slow gate 1 + a stalled gate 2 push the warning past the ceiling.
    useEffect(() => {
        const isShowingLoader = failureType === "none" && (!isReady || !sdk) && !skip;
        if (!isShowingLoader) {
            loaderShownAtRef.current = null;
            return;
        }

        loaderShownAtRef.current ??= performance.now();
        const remainingMs = Math.max(0, SDK_LOAD_HANG_WARN_MS - (performance.now() - loaderShownAtRef.current));

        const timer = setTimeout(() => {
            logger.warn(
                {
                    flow: LogFlow.WorkspaceLoading,
                    serviceProvider,
                    audioProvider,
                    employeeId: employeeNum,
                    isReady,
                    hasAgentSdk: !!agentSdk,
                    hasSdk: !!sdk,
                },
                "Workspace loader still showing — expert may be stuck",
            );
        }, remainingMs);

        return () => clearTimeout(timer);
    }, [failureType, isReady, sdk, agentSdk, skip, serviceProvider, audioProvider, employeeNum]);

    useEffect(() => {
        if (!sdk || !isReady || !!errorInitializingMsg) {
            return;
        }

        const [partner] = sdk.getPartners();
        updateDispatcherPartner(partner);
    }, [sdk, isReady, errorInitializingMsg]);

    // NOTE: This error message can be set via an async callback in the constructor. That way
    //       if any errors are thrown during an async initialization, we can catch them and display
    //       the message here
    if (failureType !== "none") {
        return { errorMessage: errorInitializingMsg, failureType };
    }

    if (!sdk || !isReady) {
        return { loadingMessage: "Getting things ready...", failureType: "none" };
    }

    return { sdk, failureType: "none" };
}
